import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

const MAINTENANCE_KEY = 'maintenance_mode';

export const maintenanceService = {
  /**
   * Get current maintenance mode status
   */
  async getMaintenanceStatus() {
    try {
      const { data, error } = await supabase?.from('app_settings')?.select('value')?.eq('key', MAINTENANCE_KEY)?.maybeSingle();

      if (error) {
        logger?.error('Failed to fetch maintenance status', error);
        return { enabled: false, message: '', estimatedReturn: '' };
      }

      if (!data) {
        return { enabled: false, message: '', estimatedReturn: '' };
      }

      const val = data?.value;
      return {
        enabled: val?.enabled === true,
        message: val?.message || '',
        estimatedReturn: val?.estimated_return || ''
      };
    } catch (err) {
      logger?.error('Maintenance status fetch error', err);
      return { enabled: false, message: '', estimatedReturn: '' };
    }
  },

  /**
   * Update maintenance mode settings (officers only)
   */
  async updateMaintenanceSettings({ enabled, message, estimatedReturn, updatedBy }) {
    try {
      const { data, error } = await supabase?.from('app_settings')?.upsert({
          key: MAINTENANCE_KEY,
          value: jsonb_build_object_js({ enabled, message, estimatedReturn, updatedBy }),
          description: 'Controls maintenance mode - when enabled, non-officer users see the maintenance page',
          updated_by: updatedBy,
          updated_at: new Date()?.toISOString()
        }, { onConflict: 'key' })?.select()?.maybeSingle();

      if (error) {
        logger?.error('Failed to update maintenance settings', error);
        return { error };
      }

      logger?.audit('MAINTENANCE_MODE_UPDATED', null, { enabled, updatedBy });
      return { data, error: null };
    } catch (err) {
      logger?.error('Maintenance settings update error', err);
      return { error: { message: 'Failed to update maintenance settings' } };
    }
  },

  /**
   * Subscribe to real-time maintenance mode changes
   */
  subscribeToMaintenanceChanges(callback) {
    const channel = supabase?.channel('maintenance_mode_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'app_settings',
          filter: `key=eq.${MAINTENANCE_KEY}`
        },
        (payload) => {
          const val = payload?.new?.value;
          if (val) {
            callback({
              enabled: val?.enabled === true,
              message: val?.message || '',
              estimatedReturn: val?.estimated_return || ''
            });
          }
        }
      )?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

// Helper to build the JSONB value object
function jsonb_build_object_js({ enabled, message, estimatedReturn, updatedBy }) {
  return {
    enabled: Boolean(enabled),
    message: message || '',
    estimated_return: estimatedReturn || '',
    updated_by: updatedBy || '',
    updated_at: new Date()?.toISOString()
  };
}

export default maintenanceService;
